/**
 * Hook for managing card flip state
 * Extracts flip animation state management from components
 */

import { useState } from 'react';
import type { CardFlipState, FlippedResponse } from '@/types/flipped';
import type { FallacyDefinition } from '@/constants/fallacyDefinitions';
import { useFallacyAnalysis } from '@/hooks/ai/useFallacyAnalysis';

export function useCardFlip() {
  const [cardFlipStates, setCardFlipStates] = useState<CardFlipState>({});
  const { generateFlippedApproach } = useFallacyAnalysis();

  const toggleCardFlip = async (name: string, fallacy: FallacyDefinition) => {
    const currentState = cardFlipStates[name];
    
    // Toggle off if already showing flipped side
    if (currentState?.isFlipped && currentState.data) {
      setCardFlipStates(prev => ({
        ...prev,
        [name]: { ...prev[name], isFlipped: false }
      }));
      return;
    }

    // Show cached data if available
    if (currentState?.data && !currentState.isFlipped) {
      setCardFlipStates(prev => ({
        ...prev,
        [name]: { ...prev[name], isFlipped: true }
      }));
      return;
    }

    // Generate new constructive approach via AI
    setCardFlipStates(prev => ({
      ...prev,
      [name]: { isFlipped: true, data: null }
    }));

    try {
      const data = await generateFlippedApproach.mutateAsync({
        name: fallacy.name,
        severity: fallacy.severity,
        category: fallacy.category,
        description: fallacy.description,
        examples: fallacy.examples,
        alternativeNames: fallacy.alternativeNames,
      });
      
      setCardFlipStates(prev => ({
        ...prev,
        [name]: { isFlipped: true, data }
      }));
    } catch (error) {
      setCardFlipStates(prev => ({
        ...prev,
        [name]: { isFlipped: false, data: null }
      }));
    }
  };

  const getFlipState = (name: string) => {
    return cardFlipStates[name] || { isFlipped: false, data: null };
  };

  const getFlippedData = (name: string): FlippedResponse | null => {
    return cardFlipStates[name]?.data || null;
  };

  return {
    cardFlipStates,
    toggleCardFlip,
    getFlipState,
    getFlippedData,
    isGenerating: generateFlippedApproach.isPending,
  };
}
